// ─── Beetsol MUI Theme ────────────────────────────────────────────────────────
// Single source of truth for all design tokens.
// Import and wrap your app root with <ThemeProvider theme={theme}>.
// Every MUI component inherits these values automatically.
// Non-MUI components (plain CSS/HTML) use the CSS variables injected via
// GlobalStyles at the bottom of this file.
// ─────────────────────────────────────────────────────────────────────────────

import { createTheme } from '@mui/material/styles';

// ── Raw token values ──────────────────────────────────────────────────────────
export const tokens = {
  // Colours
  color: {
    // Backgrounds — aligned to website palette (warm, layered):
    //   page canvas = warm #F7F7F6, surfaces = white/off-white, cream = #f1efe8
    bgPrimary:    '#FFFFFF',  // cards / raised surfaces
    bgSecondary:  '#F7F7F6',  // page canvas (website body color)
    bgTertiary:   '#F0F0EE',  // subtle fills, track backgrounds
    bgCream:      '#f1efe8',  // warm accent surfaces (website cream — tags, callouts, accent boxes)
    bgSection:    '#f8f8f6',  // section backgrounds (website feature/outcome cards)

    // Text
    textPrimary:   '#111110',
    textSecondary: '#6F6F6C',
    textTertiary:  '#9A9A96',
    // Text and icons on a dark surface such as the black player (K2.2 pt 5e). ContentPlayer.jsx reads this token in
    // 19 places (since commit e3dc69c, 25 Aug) and it was never defined, so the player's title in its top bar and the
    // caption under Play showed near-black on the black player.
    contrastText:  '#FFFFFF',

    // Borders
    borderPrimary:   '#C8C8C4',
    borderSecondary: '#E0E0DC',
    borderTertiary:  '#E8E8E6',

    // Brand / accent
    brand:       '#185FA5', // workspace mark, alert border, active states
    brandLight:  '#E8F0F9', // active nav bg, avatar bg

    // Semantic
    positive:    '#1D9E75',
    warning:     '#BA7517',
    alertDot:    '#E24B4A',
    alertBorder: '#F0997B',

    // Sidebar (dark)
    sidebarBg:      '#16161A',
    sidebarText:    '#9A9A96',
    sidebarActive:  '#FFFFFF',
    sidebarHoverBg: 'rgba(255,255,255,0.06)',
    sidebarActiveBg:'rgba(255,255,255,0.09)',

    // Status badge pairs (text on tint). Tokenized so the previously
    // hardcoded badge colours scattered across Users/Content/Item views
    // live in one place and cannot drift. Dark-text-on-light-tint only.
    status: {
      activeText:  '#0F6E56', activeBg:  '#E1F5EE', // Active / positive
      warnText:    '#854F0B', warnBg:    '#FAEEDA', // Invited / pending / warning
      alertText:   '#A32D2D', alertBg:   '#FCEBEB', // Inactive / error
      draftText:   '#6F6F6C', draftBg:   '#F0F0EE', // Draft / neutral
      infoText:    '#0E3F6E', infoBg:    '#E8F0F9', // Information notice (Add content's from-a-search banner)
    },
  },

  // Typography
  font: {
    family: '"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    size: {
      xs:      '11px',
      sm:      '12px',
      md:      '13px',
      lg:      '15px',
      xl:      '18px',
      xxl:     '21px',
      display: '24px', // page-level / hero headings — one step above h1(xxl)
    },
    weight: {
      regular: 400,
      medium:  500,
    },
    letterSpacing: {
      label: '0.05em',
    },
  },

  // Spacing (8px base grid)
  spacing: {
    xs:  '4px',
    sm:  '8px',
    md:  '12px',
    lg:  '16px',
    xl:  '24px',
    xxl: '32px',
  },

  // Border radius
  radius: {
    sm: '6px',
    md: '10px',
    lg: '14px',
    pill: '20px',
  },

  // Sidebar dimensions
  sidebar: {
    widthOpen:   '220px',
    widthClosed: '52px',
  },

  // Topnav
  topnav: {
    height: '52px',
  },

  // Transitions
  transition: '0.2s ease',

  // Breakpoints (px). Nothing pre-existing to preserve here — set to MUI's
  // own defaults so nothing shifts on adoption. Adjust if the product has a
  // different responsive contract (e.g. a specific tablet cutoff for the
  // card-collapse table pattern in Part C).
  breakpoints: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
  },
};

// A table's page count, '1 to 10 of 25' (K2.1 pt 2.1); a count still unknown reads 'more than 10'.
const pageCountLabel = ({ from, to, count }) => `${from} to ${to} of ${count !== -1 ? count : `more than ${to}`}`;

// ── MUI Theme ─────────────────────────────────────────────────────────────────
const theme = createTheme({
  // ── Palette ────────────────────────────────────────────────────────────────
  palette: {
    primary: {
      main:         tokens.color.textPrimary,
      light:        tokens.color.textSecondary,
      contrastText: '#FFFFFF',
    },
    text: {
      primary:   tokens.color.textPrimary,
      secondary: tokens.color.textSecondary,
      disabled:  tokens.color.textTertiary,
    },
    background: {
      default: tokens.color.bgSecondary,
      paper:   tokens.color.bgPrimary,
    },
    divider: tokens.color.borderTertiary,
    error: {
      main: tokens.color.alertDot,
    },
    success: {
      main: tokens.color.positive,
    },
    warning: {
      main: tokens.color.warning,
    },

    // ── Compatibility shims ──────────────────────────────────────────────────
    // The previous theme defined custom palette keys (blueShade, greyShade,
    // sidebar). No current consumer was found, but these are retained as
    // neutral, on-system aliases so any unseen reader cannot throw at runtime.
    // Remapped to the new palette (no blue): blueShade -> textPrimary family.
    blueShade: {
      main:  tokens.color.textPrimary,
      light: tokens.color.bgSecondary,
      dark:  '#000000',
      contrastText: '#FFFFFF',
    },
    greyShade: {
      main:  tokens.color.textSecondary,
      light: tokens.color.bgTertiary,
      dark:  tokens.color.textPrimary,
      contrastText: '#FFFFFF',
    },
    sidebar: {
      main:  tokens.color.sidebarBg,
      light: tokens.color.sidebarText,
      contrastText: tokens.color.sidebarActive,
    },
  },

  // ── Typography ─────────────────────────────────────────────────────────────
  typography: {
    fontFamily: tokens.font.family,
    fontSize:   13,
    fontWeightRegular: tokens.font.weight.regular,
    fontWeightMedium:  tokens.font.weight.medium,

    // NOTE (JS project, no TS augmentation needed): this is a genuinely new
    // variant, not a restyle of h1. Use via <Typography variant="display">.
    // If any consumer needs it through the `variant` prop typed in TS
    // elsewhere, augment TypographyPropsVariantOverrides there.
    display: { fontSize: tokens.font.size.display, fontWeight: tokens.font.weight.medium, letterSpacing: '-0.02em', lineHeight: 1.3 },
    h1: { fontSize: tokens.font.size.xxl, fontWeight: tokens.font.weight.medium, letterSpacing: '-0.02em' },
    h2: { fontSize: tokens.font.size.xl,  fontWeight: tokens.font.weight.medium, letterSpacing: '-0.02em' },
    h3: { fontSize: tokens.font.size.lg,  fontWeight: tokens.font.weight.medium },
    h4: { fontSize: tokens.font.size.md,  fontWeight: tokens.font.weight.medium },
    h5: { fontSize: tokens.font.size.md,  fontWeight: tokens.font.weight.medium },
    h6: { fontSize: tokens.font.size.md,  fontWeight: tokens.font.weight.medium },

    body1:   { fontSize: tokens.font.size.md, lineHeight: 1.6 },
    body2:   { fontSize: tokens.font.size.sm, lineHeight: 1.5 },
    caption: { fontSize: tokens.font.size.xs, color: tokens.color.textSecondary },

    button: {
      fontSize:      tokens.font.size.sm,
      fontWeight:    tokens.font.weight.medium,
      textTransform: 'none', // remove MUI uppercase on buttons
      letterSpacing: 'normal',
    },
  },

  // ── Spacing ────────────────────────────────────────────────────────────────
  // Array form: index N -> theme.spacing(N). Indices line up with the token
  // scale (1=xs...6=xxl) so `theme.spacing(3)` and `tokens.spacing.md` agree.
  // sx={{ p: 2 }} etc. now resolve to the design grid instead of MUI's
  // default 8px-per-unit assumption.
  spacing: [
    '0px',
    tokens.spacing.xs,
    tokens.spacing.sm,
    tokens.spacing.md,
    tokens.spacing.lg,
    tokens.spacing.xl,
    tokens.spacing.xxl,
  ],

  // ── Breakpoints ────────────────────────────────────────────────────────────
  breakpoints: {
    values: tokens.breakpoints,
  },

  // ── Shape ──────────────────────────────────────────────────────────────────
  shape: {
    borderRadius: 6, // MUI default used by components — maps to radius.sm
  },

  // ── Component overrides ────────────────────────────────────────────────────
  components: {

    // ── CssBaseline ──────────────────────────────────────────────────────────
    MuiCssBaseline: {
      styleOverrides: {
        // COMPAT: legacy CSS variables previously injected by App.js's inline
        // theme. Kept (any legacy .css may read them) but remapped to the warm
        // palette so legacy surfaces inherit the website colours too.
        ':root': {
          '--primary-color':      tokens.color.textPrimary,
          '--secondary-color':    tokens.color.textPrimary,   // was blue-slate #2c3e50 — retired
          '--text-primary':       tokens.color.textPrimary,
          '--text-secondary':     tokens.color.textSecondary,
          '--text-light':         tokens.color.textTertiary,
          '--background-default': tokens.color.bgSecondary,
          '--background-paper':   tokens.color.bgPrimary,
          '--background-light':   tokens.color.bgTertiary,
          '--border-primary':     tokens.color.borderPrimary,
          '--border-secondary':   tokens.color.borderSecondary,
          '--border-tertiary':    tokens.color.borderTertiary,
          '--alert-dot':          tokens.color.alertDot,
          '--alert-border':       tokens.color.alertBorder,
          '--alert-text':         tokens.color.status.alertText,

        },
        '*': { boxSizing: 'border-box' },
        // COMPAT: layout rules carried over from the old inline theme —
        // forms were built against these.
        '.MuiFormControl-root': { margin: '0 !important' },
        '.ql-toolbar.ql-snow':  { borderRadius: '12px 12px 0 0' },
        '.ql-container.ql-snow':{ borderRadius: '0 0 12px 12px' },
        body: {
          fontFamily:            tokens.font.family,
          backgroundColor:       tokens.color.bgSecondary,
          color:                 tokens.color.textPrimary,
          WebkitFontSmoothing:   'antialiased',
          MozOsxFontSmoothing:   'grayscale',
        },
        // Thin scrollbars globally
        '::-webkit-scrollbar':       { width: '6px', height: '6px' },
        '::-webkit-scrollbar-track': { background: tokens.color.bgTertiary, borderRadius: '3px' },
        '::-webkit-scrollbar-thumb': { background: tokens.color.borderPrimary, borderRadius: '3px' },
        '::-webkit-scrollbar-thumb:hover': { background: tokens.color.textTertiary },
        // Hide scrollbar inside sidebar
        '.sidebar::-webkit-scrollbar':           { display: 'none' },
        '.MuiDrawer-paper::-webkit-scrollbar':   { display: 'none' },
      },
    },

    // ── AppBar / TopNav ───────────────────────────────────────────────────────
    MuiAppBar: {
      defaultProps:  { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: tokens.color.bgPrimary,
          borderBottom:    `0.5px solid ${tokens.color.borderTertiary}`,
          height:          tokens.topnav.height,
          zIndex:          1201,
        },
      },
    },

    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight:   `${tokens.topnav.height} !important`,
          paddingLeft:  tokens.spacing.lg,
          paddingRight: tokens.spacing.lg,
        },
      },
    },

    // ── Drawer / Sidebar ──────────────────────────────────────────────────────
    MuiDrawer: {
      styleOverrides: {
        paper: {
          width:           tokens.sidebar.widthOpen,
          backgroundColor: tokens.color.sidebarBg,
          color:           tokens.color.sidebarText,
          border:          'none',
          overflowX:       'hidden',
          transition:      `width ${tokens.transition}`,
        },
      },
    },

    // ── Buttons ───────────────────────────────────────────────────────────────
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius:  tokens.radius.sm,
          padding:       '6px 14px',
          fontSize:      tokens.font.size.sm,
          fontWeight:    tokens.font.weight.medium,
          fontFamily:    tokens.font.family,
          letterSpacing: 'normal',
          textTransform: 'none',
          transition:    `all ${tokens.transition}`,
        },
        contained: {
          backgroundColor: tokens.color.textPrimary,
          color:           tokens.color.bgPrimary,
          border:          `0.5px solid ${tokens.color.textPrimary}`,
          '&:hover': {
            backgroundColor: '#2A2A28',
          },
        },
        // One outlined style for every button that isn't the black primary (K2.3 pt 15), as the View button on a
        // learner's course page (N44 pt 7): no fill, a dark border and dark text, the tertiary background on hover.
        // Removed 2 Nov 2026 (K2.3 pt 15): the outlined button's light grey border and grey text.
        outlined: {
          backgroundColor: 'transparent',
          borderColor:     tokens.color.textPrimary,
          color:           tokens.color.textPrimary,
          '&:hover': {
            backgroundColor: tokens.color.bgTertiary,
            borderColor:     tokens.color.textPrimary,
          },
        },
        text: {
          color: tokens.color.textSecondary,
          '&:hover': {
            backgroundColor: tokens.color.bgSecondary,
            color:           tokens.color.textPrimary,
          },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.sm,
          color:        tokens.color.textSecondary,
          marginRight:  '0px', // COMPAT: carried from retired App.js inline theme
          transition:   `background ${tokens.transition}`,
          '&:hover': {
            backgroundColor: tokens.color.bgSecondary,
            color:           tokens.color.textPrimary,
          },
        },
      },
    },

    // ── Cards ─────────────────────────────────────────────────────────────────
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: tokens.color.bgPrimary,
          border:          `0.5px solid ${tokens.color.borderTertiary}`,
          borderRadius:    tokens.radius.lg,
          // HYBRID: cards get a CLEAN, even, soft shadow back for depth (the
          // intentional "lifted content" look). !important is required because
          // MuiCard inherits MuiPaper, and Paper is shadow-killed with
          // !important below — without it here, the Paper rule would win and
          // cards would stay flat. This is the EVEN, all-sides shadow — NOT the
          // heavy below-only bleed that read as dirty patches on the filters.
          boxShadow:       '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04) !important',
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: tokens.spacing.xl,
          '&:last-child': { paddingBottom: tokens.spacing.xl },
        },
      },
    },

    // ── Paper ─────────────────────────────────────────────────────────────────
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: tokens.color.bgPrimary,
          border:          `0.5px solid ${tokens.color.borderTertiary}`,
          borderRadius:    tokens.radius.md,
          // HARD-KILL the elevation shadow. defaultProps:{elevation:0} only sets
          // the default — any <Paper elevation={n}> or MUI internal (Select /
          // Popover / Menu paper) re-adds the grey drop-shadow that was showing
          // as patches under filters and a band at page bottom. This forces it
          // off regardless of the elevation prop.
          boxShadow:       'none !important',
        },
        // Keep the legit floating shadow ONLY for elevation-8+ (menus/popovers
        // that genuinely need to lift off the page get it via their own
        // component overrides below, not from Paper).
      },
    },

    // ── Inputs ────────────────────────────────────────────────────────────────
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize:   tokens.font.size.md,
          fontFamily: tokens.font.family,
          color:      tokens.color.textPrimary,
          '&.Mui-disabled': { color: tokens.color.textTertiary },
        },
        input: {
          '&::placeholder': { color: tokens.color.textTertiary, opacity: 1 },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius:  tokens.radius.sm,
          fontSize:      tokens.font.size.md,
          //backgroundColor: tokens.color.bgPrimary,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.color.borderSecondary,
            top: 0,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.color.borderPrimary,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.color.textPrimary,
            borderWidth: '1px',
          },
        },
        input: {
          padding: '8px 12px',
          '&::placeholder': { color: tokens.color.textTertiary, opacity: 1 },
        },
        // A text box with more than one line indents its text once, as a one-line box does (K2.3 pt 18): the box
        // takes the one-line field's padding and its text area none. Before, the input padding above came on top of
        // MUI's own padding on a multi-line box, so its text sat further in.
        multiline:      { padding: '8px 12px' },
        inputMultiline: { padding: 0 },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize:  tokens.font.size.md,
          color:     tokens.color.textSecondary,
          '&.Mui-focused': { color: tokens.color.textPrimary },
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize:  tokens.font.size.xs,
          marginLeft:  0,
          marginRight: 0,
          marginTop:   '4px',
          color:     tokens.color.textSecondary,
          '&.Mui-error': {
            color:      tokens.color.status.alertText,
            fontWeight: tokens.font.weight.medium,
          },
        },
      },
    },

    // ── Select ────────────────────────────────────────────────────────────────
    MuiSelect: {
      styleOverrides: {
        icon: { color: tokens.color.textSecondary },
      },
    },

    // Open dropdowns/menus DO need to lift off the page — but via a clean
    // border + soft shadow on the OPEN popover only, not the resting Paper.
    // (Paper above is shadow-killed; these add a controlled one back just for
    // the floating menu surface, so "All Authors" etc. look attached when open
    // without the resting filter boxes bleeding a shadow.)
    MuiMenu: {
      styleOverrides: {
        paper: {
          border:       `0.5px solid ${tokens.color.borderTertiary}`,
          //borderRadius: tokens.radius.md,          
          borderRadius:  '12px',
          transformOrigin:  'top right',          
          boxShadow:    '0 12px 34px rgba(20,18,12,.13), 0 2px 6px rgba(20,18,12,.06) !important',
          border:       `1px solid ${tokens.color.borderTertiary}`,
          minWidth:     '168px',
        },
        list: { padding: '4px' },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          border:       `0.5px solid ${tokens.color.borderTertiary}`,
          borderRadius: tokens.radius.md,
          boxShadow:    '0 4px 16px rgba(0,0,0,0.08) !important',
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          //fontSize:     tokens.font.size.md,
          color:        tokens.color.textPrimary,
          //borderRadius: tokens.radius.sm,
          //padding:      '7px 10px',
          margin:       '1px 0',
          minHeight:    'auto',
          transition:   `background ${tokens.transition}`,
          '&:hover':          { backgroundColor: tokens.color.bgSecondary },
          '&.Mui-selected':   {
            backgroundColor: tokens.color.brandLight,
            '&:hover':       { backgroundColor: tokens.color.brandLight },
          }, 
          gap:          '11px',
          fontSize:      '14px',
          //padding:       '9px 11px',
          borderRadius:   '8px',        
        },
      },
    },

    // ── Table ─────────────────────────────────────────────────────────────────
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontSize:      tokens.font.size.xs,
            fontWeight:    tokens.font.weight.medium,
            color:         tokens.color.textSecondary,
            letterSpacing: tokens.font.letterSpacing.label,
            textTransform: 'uppercase',
            borderBottom:  `0.5px solid ${tokens.color.borderTertiary}`,
            padding:       '10px 20px',
            backgroundColor: tokens.color.bgPrimary,
          },
        },
      },
    },

    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            borderBottom: `0.5px solid ${tokens.color.borderTertiary}`,
            transition:   `background ${tokens.transition}`,
            '&:last-child': { borderBottom: 'none' },
            '&:hover': { backgroundColor: tokens.color.bgSecondary },
          },
          '& .MuiTableCell-body': {
            fontSize:  tokens.font.size.md,
            color:     tokens.color.textPrimary,
            padding:   '12px 20px',
            border:    'none',
          },
        },
      },
    },

    MuiTableContainer: {
      styleOverrides: {
        root: {
          border:       `0.5px solid ${tokens.color.borderTertiary}`,
          borderRadius: tokens.radius.lg,
          boxShadow:    'none',
        },
      },
    },

    // ── Avatar ────────────────────────────────────────────────────────────────
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.color.brandLight, // token comment already earmarks this for avatar bg
          color:           tokens.color.brand,
          fontSize:        tokens.font.size.sm,
          fontWeight:      tokens.font.weight.medium,
        },
      },
    },

    // ── Pagination ────────────────────────────────────────────────────────────
    MuiPagination: {
      styleOverrides: {
        root: {
          '& .MuiPaginationItem-root': {
            fontSize:      tokens.font.size.sm,
            fontFamily:    tokens.font.family,
            borderRadius:  tokens.radius.sm,
            color:         tokens.color.textSecondary,
            '&:hover':          { backgroundColor: tokens.color.bgSecondary },
            '&.Mui-selected': {
              backgroundColor: tokens.color.textPrimary,
              color:           tokens.color.bgPrimary,
              '&:hover':       { backgroundColor: '#2A2A28' },
            },
          },
        },
      },
    },

    // Every table's page count reads '1 to 10 of 25' (K2.1 pt 2.1): MUI's own label writes an en dash between the
    // numbers. The data grid (MUI X) hands its pager a label of its own, so it takes this one through its locale text
    // (MuiDataGrid, below); the Insights pages' own pagers write theirs in their files (K3 pt 17).
    MuiTablePagination: {
      defaultProps: {
        labelDisplayedRows: pageCountLabel,
      },
    },

    // ── Chip / Badge ──────────────────────────────────────────────────────────
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize:     tokens.font.size.xs,
          fontWeight:   tokens.font.weight.medium,
          borderRadius: tokens.radius.sm,
          height:       '22px',
        },
        // Skill chips. Selected / earned skills render as near-black filled
        // (matching primary buttons); the picker uses a neutral outline.
        // Terracotta is intentionally NOT used here so it stays reserved for
        // the workspace mark and active nav.
        filled: {
          backgroundColor: tokens.color.textPrimary,
          color:           tokens.color.bgPrimary,
          '& .MuiChip-deleteIcon': {
            color: 'rgba(255,255,255,0.6)',
            '&:hover': { color: '#FFFFFF' },
          },
        },
        outlined: {
          borderColor: tokens.color.borderSecondary,
          color:       tokens.color.textPrimary,
          backgroundColor: tokens.color.bgPrimary,
          '& .MuiChip-deleteIcon': {
            color: tokens.color.textTertiary,
            '&:hover': { color: tokens.color.textPrimary },
          },
        },
        // Status pill colours — the ONE place tokens.color.status is read.
        // The shared StatusPill component (Part C) should render
        // <Chip color="success|warning|error|default" .../> and get these
        // automatically, instead of any component picking its own hex.
        //   success -> Active     warning -> Invited/Pending
        //   error   -> Inactive   default -> Draft / status 2 (Retired)
        colorSuccess: {
          backgroundColor: tokens.color.status.activeBg,
          color:           tokens.color.status.activeText,
        },
        colorWarning: {
          backgroundColor: tokens.color.status.warnBg,
          color:           tokens.color.status.warnText,
        },
        colorError: {
          backgroundColor: tokens.color.status.alertBg,
          color:           tokens.color.status.alertText,
        },
        colorDefault: {
          backgroundColor: tokens.color.status.draftBg,
          color:           tokens.color.status.draftText,
        },
      },
    },

    // ── Dialog / Modal ────────────────────────────────────────────────────────
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.radius.lg,
          border:       `0.5px solid ${tokens.color.borderTertiary}`,
          boxShadow:    '0 8px 32px rgba(0,0,0,0.12)',
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize:   tokens.font.size.lg,
          fontWeight: tokens.font.weight.medium,
          padding:    `${tokens.spacing.xl} ${tokens.spacing.xl} ${tokens.spacing.md}`,
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: { 
                padding: `${tokens.spacing.xl} ${tokens.spacing.xl}`,
                '.MuiDialogTitle-root + &': {
                  paddingTop: tokens.spacing.xl,
                },
                '&:first-of-type': {
                  paddingTop: tokens.spacing.xl,
                },
              },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          gap: tokens.spacing.sm,
          padding: `${tokens.spacing.md} ${tokens.spacing.xl}`,
          // A pop-up's Cancel, Close and other buttons written as text buttons take the one outlined style too
          // (K2.3 pt 15); a red (error) one keeps its colour.
          '& .MuiButton-text:not(.MuiButton-colorError)': {
            border:          `1px solid ${tokens.color.textPrimary}`,
            backgroundColor: 'transparent',
            color:           tokens.color.textPrimary,
            '&:hover': { backgroundColor: tokens.color.bgTertiary, color: tokens.color.textPrimary },
            '&.Mui-disabled': { borderColor: tokens.color.borderSecondary, color: tokens.color.textTertiary },
          },
        },
      },
    },
    // ── List items (used in sidebar) ──────────────────────────────────────────
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.sm,
          margin:       '1px 0',
          padding:      '7px 8px',
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.sm,
          margin:       '1px 0',
          padding:      '7px 8px',
          transition:   `background ${tokens.transition}`,
          '&:hover':          { backgroundColor: tokens.color.sidebarHoverBg },
          '&.Mui-selected': {
            backgroundColor: tokens.color.sidebarActiveBg,
            '&:hover':       { backgroundColor: tokens.color.sidebarActiveBg },
          },
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth:   '28px',
          color:      tokens.color.sidebarText,
          fontSize:   '18px',
        },
      },
    },

    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize:   tokens.font.size.md,
          color:      tokens.color.sidebarText,
          fontFamily: tokens.font.family,
        },
      },
    },

    // ── Divider ───────────────────────────────────────────────────────────────
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: tokens.color.borderTertiary },
      },
    },

    // ── Tooltip ───────────────────────────────────────────────────────────────
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#2A2A2E',
          color:           '#FFFFFF',
          fontSize:        tokens.font.size.xs,
          borderRadius:    tokens.radius.sm,
          padding:         '5px 10px',
        },
      },
    },

    // ── Tabs ──────────────────────────────────────────────────────────────────
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize:      tokens.font.size.sm,
          fontWeight:    tokens.font.weight.medium,
          textTransform: 'none',
          color:         tokens.color.textSecondary,
          minHeight:     '40px',
          padding:       '6px 16px',
          '&.Mui-selected': { color: tokens.color.textPrimary },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: tokens.color.textPrimary, height: '1.5px' },
        root:      { borderBottom: `0.5px solid ${tokens.color.borderTertiary}`, minHeight: '40px' },
      },
    },

    // ── Snackbar / Alert ──────────────────────────────────────────────────────
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiPaper-root': {
            borderRadius: tokens.radius.md,
            boxShadow:    '0 8px 24px rgba(0,0,0,0.14) !important',
          },
        },
        anchorOriginBottomLeft: { margin: tokens.spacing.lg },
        anchorOriginBottomRight:{ margin: tokens.spacing.lg },
        anchorOriginTopLeft:    { margin: tokens.spacing.lg },
        anchorOriginTopRight:   { margin: tokens.spacing.lg },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.md,
          fontSize:     tokens.font.size.md,
          // Default (no severity) accent — this is the previously-unused
          // alertBorder token, now actually read.
          borderLeft: `3px solid ${tokens.color.alertBorder}`,
        },
        // Severity variants pull from the same status pairs the pills use,
        // so an "error" alert and an "Inactive" pill agree on colour.
        standardSuccess: {
          backgroundColor: tokens.color.status.activeBg,
          color:           tokens.color.status.activeText,
          borderLeft:      `3px solid ${tokens.color.status.activeText}`,
          '& .MuiAlert-icon': { color: tokens.color.status.activeText },
        },
        standardWarning: {
          backgroundColor: tokens.color.status.warnBg,
          color:           tokens.color.status.warnText,
          borderLeft:      `3px solid ${tokens.color.status.warnText}`,
          '& .MuiAlert-icon': { color: tokens.color.status.warnText },
        },
        standardError: {
          backgroundColor: tokens.color.status.alertBg,
          color:           tokens.color.status.alertText,
          borderLeft:      `3px solid ${tokens.color.status.alertText}`,
          '& .MuiAlert-icon': { color: tokens.color.status.alertText },
        },
        standardInfo: {
          backgroundColor: tokens.color.status.draftBg,
          color:           tokens.color.status.draftText,
          borderLeft:      `3px solid ${tokens.color.status.draftText}`,
          '& .MuiAlert-icon': { color: tokens.color.status.draftText },
        },
        // Toasts (the filled alerts) show one solid colour with a white close button (K2.3 pt 19): no accent strip
        // from root above (it showed salmon on a green success toast), and the close button keeps the toast's own
        // white text colour, hovered too, instead of the icon buttons' grey.
        filled: {
          borderLeft: 'none',
          '& .MuiAlert-action .MuiIconButton-root': {
            color: 'inherit',
            '&:hover': { color: 'inherit', backgroundColor: 'rgba(255,255,255,0.16)' },
          },
        },
      },
    },

    // ── Badge ─────────────────────────────────────────────────────────────────
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontSize:     tokens.font.size.xs,
          fontWeight:   tokens.font.weight.medium,
          minWidth:     '16px',
          height:       '16px',
          padding:      '0 4px',
          borderRadius: '8px',
        },
      },
    },

    // Remove box shadow from containers we don't want elevated
    MuiContainer: {
      styleOverrides: {
        root: { boxShadow: 'none'},
      },
    },

    // ── COMPAT (carried over from the retired App.js inline theme) ───────────
    // Forms across the app were laid out against these; removing them when the
    // inline theme was deleted would shift checkbox/radio/switch alignment and
    // float every TextField label. Visual behavior preserved, colours inherit
    // from the palette above.
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        // NOTE: the retired inline theme set InputLabelProps:{shrink:false}
        // globally. That suppresses the floating-label animation everywhere and
        // can make a filled field look mislabeled. It's removed — fields that
        // genuinely need a static label should set shrink locally. UserProfile
        // and the auth forms use placeholders, so they're unaffected.
      },
      styleOverrides: {
        root: { margin: '0 !important' },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: '0 0 0 9px',
          '&.Mui-checked':               { color: tokens.color.textPrimary },
          '&.MuiCheckbox-indeterminate': { color: tokens.color.textPrimary },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: { padding: '0 0 0 9px' },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: { margin: 0 },
        switchBase: {
          '&.Mui-checked': {
            color: tokens.color.bgPrimary,
            '& + .MuiSwitch-track': {
              backgroundColor: tokens.color.brand,
              opacity: 1,
            },
          },
        },
        track: {
          backgroundColor: tokens.color.borderPrimary,
          opacity: 1,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 8,
          borderRadius: 999,
          backgroundColor: tokens.color.bgTertiary,
        },
        bar: {
          borderRadius: 999,
          backgroundColor: tokens.color.brand,
        },
        colorPrimary: {
          backgroundColor: tokens.color.bgTertiary,
        },
        colorSuccess: {
          backgroundColor: tokens.color.positive,
        },
      },
    },
    MuiDataGrid: {
      // The page count, as every table's (K2.1 pt 2.1, MuiTablePagination above).
      defaultProps: {
        localeText: { MuiTablePagination: { labelDisplayedRows: pageCountLabel } },
      },
      styleOverrides: {
        root: { borderRadius: '12px' },
      },
    },
  },
});

export default theme;